import { useEffect, useMemo, useState } from 'react';
import { API } from '../../../../../api';
import type { TierAction } from '../../../../../components/gamification/DevelopmentTierPanel';
import type { MemberProgramCardData } from './types';

/**
 * The learner's outstanding work, grouped by the Development Tier measure it
 * moves. Feeds the How to Improve dropdown beneath each measure on
 * DevelopmentTierPanel: the generic guidance says "retake failed quizzes", this
 * says WHICH lessons.
 *
 * Sources:
 *  - Knowledge Retention / Knowledge Mastery — GET /elliott-improve-queue/{grpId}
 *    per program that still has unresolved lessons. The queue already splits
 *    quiz items (still-wrong MCQs) from scenario items (unresolved Live
 *    Scenarios), which is exactly the split between the two measures.
 *  - Lesson Completion / Learning Range — derived from the framework payload
 *    the dashboard already holds (resume lesson, and programs never started).
 *  - Proactive Learning — no per-item work; the action is to go and ask Elliott.
 *
 * Org Members reuses it for an admin's view of a member's tier: pass the
 * member's id and leave out the callbacks, and the same rows render inert.
 */

// BE section display names (GET /gamification/learner/summary → medals.sections[].name).
// SECTION_HELP in scorecardData.ts is keyed by the same strings.
export const MEASURE = {
  lessonCompletion: 'Lesson Completion',
  knowledgeRetention: 'Knowledge Retention',
  knowledgeMastery: 'Knowledge Mastery',
  learningRange: 'Learning Range',
  proactiveLearning: 'Proactive Learning'
} as const;

/** One row of GET /elliott-improve-queue/{grpId}, plus the program it came from. */
interface ImproveEntry {
  programId: string;
  programName: string;
  levelId: string;
  taskId: string;
  moduleTitle: string;
  lessonTitle: string;
  type: 'scenario' | 'quiz';
  questionCount: number;
}

/**
 * The program fields the actions are built from. The learner dashboard's cards
 * satisfy it as they are; Org Members maps its list rows onto it.
 */
export type TierProgram = Pick<
  MemberProgramCardData,
  'id' | 'name' | 'roles' | 'status' | 'unresolvedLessons' | 'locked' | 'resumeLesson'
>;

interface Params<P extends TierProgram> {
  programs: P[];
  /** Whose queue to read — omit for the caller's own; an admin passes the member's id. */
  memberId?: string;
  /** Open a lesson in Improve Performance mode (?improve=1). Omit to render the rows inert. */
  onOpenImprove?: (programId: string, levelId: string, taskId: string) => void;
  /** Resume / start a program from its next lesson. */
  onContinueProgram?: (program: P) => void;
  /** Send the learner to Elliott. */
  onAskElliott?: () => void;
}

const isLearnerProgram = (p: TierProgram) => p.roles.length === 0 || p.roles.includes('mentee');

export const useTierActions = <P extends TierProgram>({
  programs,
  memberId,
  onOpenImprove,
  onContinueProgram,
  onAskElliott
}: Params<P>): { actionsByMeasure: Record<string, TierAction[]>; loading: boolean } => {
  const [entries, setEntries] = useState<ImproveEntry[]>([]);
  const [loading, setLoading] = useState(false);

  // Programs worth asking about: the learner's own, still flagged, not locked
  // (the BE rejects the queue with 423 while a program is locked).
  const queueTargets = useMemo(
    () =>
      programs
        .filter((p) => p.unresolvedLessons > 0 && p.locked !== true && isLearnerProgram(p))
        .map((p) => ({ id: p.id, name: p.name })),
    [programs]
  );
  // Stable dependency — queueTargets is a new array each render.
  const targetKey = queueTargets.map((t) => t.id).join(',');

  useEffect(() => {
    if (queueTargets.length === 0) {
      setEntries([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.allSettled(
      queueTargets.map((target) =>
        API.getElliottImproveQueue({ groupId: target.id, memberId }).then((res) => ({ target, res }))
      )
    )
      .then((results) => {
        if (cancelled) return;
        const next: ImproveEntry[] = [];
        results.forEach((result) => {
          // One program's queue failing (locked mid-session, permissions) must
          // not blank the whole panel — the rest still render.
          if (result.status !== 'fulfilled') return;
          const { target, res } = result.value;
          // A BE that predates ?memberId= answers with the CALLER's queue, so
          // only a response that echoes the member back is theirs.
          if (memberId && res.data?.memberId !== memberId) return;
          (res.data?.items || []).forEach((item: any, index: number) => {
            next.push({
              programId: target.id,
              programName: target.name,
              levelId: item.levelId,
              taskId: item.taskId,
              moduleTitle: item.moduleTitle || '',
              lessonTitle: item.lessonTitle || `Lesson ${index + 1}`,
              type: item.type === 'quiz' ? 'quiz' : 'scenario',
              questionCount: Array.isArray(item.questionIds) ? item.questionIds.length : 0
            });
          });
        });
        setEntries(next);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetKey, memberId]);

  const actionsByMeasure = useMemo(() => {
    const fromQueue = (type: 'quiz' | 'scenario'): TierAction[] =>
      entries
        .filter((e) => e.type === type)
        .map((e) => ({
          id: `${e.programId}-${e.taskId}-${e.type}`,
          programName: e.programName,
          label: e.moduleTitle ? `${e.moduleTitle} · ${e.lessonTitle}` : e.lessonTitle,
          note:
            type === 'quiz' && e.questionCount > 0
              ? `${e.questionCount} question${e.questionCount === 1 ? '' : 's'}`
              : undefined,
          onClick: onOpenImprove ? () => onOpenImprove?.(e.programId, e.levelId, e.taskId) : undefined
        }));

    const learnerPrograms = programs.filter(isLearnerProgram);

    // Lesson Completion — the next unfinished lesson in each program already
    // under way. Programs never opened belong to Learning Range instead, so a
    // learner isn't told the same thing twice under two measures.
    const lessonCompletion: TierAction[] = learnerPrograms
      .filter((p) => p.status === 'in-progress' && p.resumeLesson)
      .map((p) => ({
        id: `complete-${p.id}`,
        programName: p.name,
        label: p.resumeLesson?.lessonName || '',
        ctaLabel: 'Open This Lesson',
        onClick: p.locked || !onContinueProgram ? undefined : () => onContinueProgram?.(p)
      }));

    // Starting a program is a single action, so the row IS the button — the
    // program name is the only context needed above it. Locked programs can't
    // be started, so Learning Range leaves them out (BE does the same).
    const learningRange: TierAction[] = learnerPrograms
      .filter((p) => p.status === 'not-started' && !p.locked)
      .map((p) => ({
        id: `start-${p.id}`,
        programName: p.name,
        label: '',
        ctaLabel: 'Open This Program',
        onClick: p.locked || !onContinueProgram ? undefined : () => onContinueProgram?.(p)
      }));

    const proactiveLearning: TierAction[] = [
      {
        id: 'ask-elliott',
        programName: '',
        label: '',
        ctaLabel: 'Ask Elliott a Question',
        onClick: onAskElliott
      }
    ];

    return {
      [MEASURE.lessonCompletion]: lessonCompletion,
      [MEASURE.knowledgeRetention]: fromQueue('quiz'),
      [MEASURE.knowledgeMastery]: fromQueue('scenario'),
      [MEASURE.learningRange]: learningRange,
      [MEASURE.proactiveLearning]: proactiveLearning
    };
  }, [entries, programs, onOpenImprove, onContinueProgram, onAskElliott]);

  return { actionsByMeasure, loading };
};

export default useTierActions;
